import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Terms from "../globals/Terms";
import PrivacyPolicy from "../globals/PrivacyPolicy";
import PasswordInput from "../elements/PasswordInput";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { appRoutes } from "../../routes/appRoutes";

interface JoinWorkspaceProps {
    workspace: string;
    fullname: string;
    email: string;
    password: string;
    confirmpassword: string;
}

const JoinWorkspace: React.FC = () => {

    const navigate = useNavigate();

    const [formData, setFormData] = useState<JoinWorkspaceProps>({
        workspace: "Prismfly",
        email: "data@prismfly.com",
        fullname: "",
        password: "",
        confirmpassword: ""
    })

    const [passwordConstraintClass, setPassWordConstraintClass] = useState<string>("bg-background-surface-01 hidden flex-col gap-[8px] my-[8px] p-[8px] rounded-[6px] w-full ");

    const handleSubmit = () => {
        navigate(appRoutes.experiments.root)
    };

    const handleDisablePaste = (e:  React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }))
        if (name === "password") {
            setPassWordConstraintClass("bg-background-surface-01 flex flex-col gap-[8px] my-[8px] p-[8px] rounded-[6px] w-full ");
            if (e.target.closest('.ui-password-wrapper')) {
                const currentTarget = e.target as HTMLElement
                const passwordWrapper = currentTarget.closest('.ui-password-wrapper')
                const passwordConstraint = passwordWrapper?.nextElementSibling
                const confirmPasswordError = passwordWrapper?.querySelector('.confirm-password-error')
                if (confirmPasswordError) {
                    if (value === formData.confirmpassword) {
                        confirmPasswordError.textContent = ""
                    } else {
                        confirmPasswordError.textContent = "Passwords do not match."
                    }
                }
                const letterRegex = /[A-Z]/;
                const numberRegex = /\d/;
                const specialCharacterRegex = /[^\w\s]/;
    
                const hasNumbers = numberRegex.test(value);
                const hasLetters = letterRegex.test(value);
                const hasSpecialCharacter = specialCharacterRegex.test(value);

                const passwordHasLetter = passwordConstraint?.querySelector('.password-constraints__letter') as HTMLElement
                const passwordHasNumber = passwordConstraint?.querySelector('.password-constraints__number') as HTMLElement
                const passwordHasSpecialCharacter = passwordConstraint?.querySelector('.password-constraints__special-character') as HTMLElement
                const passwordLength = passwordConstraint?.querySelector('.password-constraints__length') as HTMLElement
                if (value.length >= 8) {
                    passwordLength.textContent = "✔️"
                    passwordLength.nextElementSibling?.classList.add('text-state-success')
                } else {
                    passwordLength.textContent = "❌"
                    passwordLength.nextElementSibling?.classList.remove('text-state-success')
                }
                if (hasLetters) {
                    passwordHasLetter.textContent = "✔️"
                    passwordHasLetter.nextElementSibling?.classList.add('text-state-success')
                } else {
                    passwordHasLetter.textContent = "❌"
                    passwordHasLetter.nextElementSibling?.classList.remove('text-state-success')
                }
                if (hasNumbers) {
                    passwordHasNumber.textContent = "✔️"
                    passwordHasNumber.nextElementSibling?.classList.add('text-state-success')
                } else {
                    passwordHasNumber.textContent = "❌"
                    passwordHasNumber.nextElementSibling?.classList.remove('text-state-success')
                }
                if (hasSpecialCharacter) {
                    passwordHasSpecialCharacter.textContent = "✔️"
                    passwordHasSpecialCharacter.nextElementSibling?.classList.add('text-state-success')
                } else {
                    passwordHasSpecialCharacter.textContent = "❌"
                    passwordHasSpecialCharacter.nextElementSibling?.classList.remove('text-state-success')
                }
            }
        }
        if (name === "confirmpassword") {
            const currentTarget = e.target as HTMLElement
            if (currentTarget.closest('.ui-password-wrapper')) {
                const passwordWrapper = currentTarget.closest('.ui-password-wrapper');
                const errorText = passwordWrapper?.querySelector('.confirm-password-error') as HTMLElement
                if (formData.password === value) {
                    errorText.textContent = ""
                } else {
                    errorText.textContent = "Passwords do not match."
                }
            }
        }
    }

    return (
        <div>
            <div className="mb-[32px] text-center w-full">
                <h2 className="type-heading-lg">Get Started</h2>
                <p className="mt-[8px] text-body-muted text-sm">Sign up now to unlock insights, optimize performance, and elevate your digital experience.</p>
            </div>
            <form method="PUT" className="flex flex-col items-center" onSubmit={handleSubmit} >
                <Input type="text" label="Workspace" name="workspace" id="ui-panel-register_workspace_input" onChange={handleChange} required value={formData.workspace} readOnly disabled />
                <Input type="text" label="Email" name="email" id="ui-panel-register_email_input" onChange={handleChange} required value={formData.email} readOnly disabled />
                <Input type="text" label="Full name" name="fullname" id="ui-panel-register_name_input" onChange={handleChange} placeholder="John Smith" required value={formData.fullname} />
                <div className="field mt-[8px] ui-password-wrapper">
                    <label htmlFor="new-password" className="field-label">
                        Password
                    </label>
                    <PasswordInput name="password" id="new-password" value={formData.password} onChange={handleChange} />
                    <label htmlFor="confirm-password" className="field-label mt-[8px]">
                        Confirm Password
                    </label>
                    <PasswordInput name="confirmpassword" id="confirm-password" value={formData.confirmpassword} onChange={handleChange} onPaste={handleDisablePaste} placeholder="Type the same password" />
                    <small className="field-error confirm-password-error block mt-[6px]"></small>
                </div>
                <div className={passwordConstraintClass} id="password-constraints">
                    <span className="ui-password-requirement flex gap-[8px] items-center leading-[1] text-xs" data-password-requirement="^.{8,}$" data-password-requirement-met="false">
                        <span className="password-constraints__length">
                            ❌
                        </span>
                        <span>Password must be at least 8 characters long.</span>
                    </span>
                    <span className="ui-password-requirement flex gap-[8px] items-center leading-[1] text-xs" data-password-requirement="[A-Z]" data-password-requirement-met="false">
                        <span className="password-constraints__letter">
                            ❌
                        </span>
                        <span>Include at least one uppercase letter (A-Z).</span>
                    </span>
                    <span className="ui-password-requirement flex gap-[8px] items-center leading-[1] text-xs" data-password-requirement="[0-9]" data-password-requirement-met="false">
                        <span className="password-constraints__number">
                            ❌
                        </span>
                        <span>Include at least one numerical digit (0-9).</span>
                    </span>
                    <span className="ui-password-requirement flex gap-[8px] items-center leading-[1] text-xs" data-password-requirement="\W|\_" data-password-requirement-met="false">
                        <span className="password-constraints__special-character">
                            ❌
                        </span>
                        <span>Include at least one special character (e.g., !, @, #, $).</span>
                    </span>
                </div>
                <Button type="button" name="submit" aria-label="Create account">
                    Join Workspace
                </Button>
            </form>
            <div className="mt-[8px] text-center w-full">
                <div className="block mb-0 mt-[16px] text-md text-center w-full">By submitting this form, you agree to the <Terms /> and <PrivacyPolicy />.</div>
                <div className="block mt-[16px] text-md text-center w-full">
                    Already have an account?
                    <NavLink to="/login" className="text-heading text-sm font-semibold leading-5"> Login
                    </NavLink>
                </div>
            </div>
        </div>
    )
}

export default JoinWorkspace
